import { map, catchError } from 'rxjs/operators';
import {
  HttpEventType,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges
} from '@angular/core';
import { DataService, DocumentService, WorkflowContextService, WorkflowService } from '../../services';
import numeral from 'numeral';
import { Document } from 'src/app/models';
import { CommonButtonData } from '../common/common-button/common-button.component.model';
import { CommonConfirmationModalData } from '../common/common-confirmation-modal/common-confirmation-modal.component.model';
import { LayoutService } from 'src/app/services/layout.service';
import { throwError } from 'rxjs/internal/observable/throwError';
import { svgs } from 'src/assets/export.assets';

export interface UploadResponse {
  id: string;
  name: string;
  parentId: string;
  parentPath: string;
  parentType: string;
  path: string;
  pathName: string;
}

interface UploadFile {
  data: File;
  inProgress: boolean;
  progress: number;
  size: any;
  finished: boolean;
}

@Component({
    selector: 'wm-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.css'],
    standalone: false
})
export class UploadComponent implements OnInit {
  @ViewChild('uploadButton', { static: true }) uploadButton: ElementRef;
  @ViewChild('fileUpload', { static: false }) fileUpload: ElementRef;//static chnaged from true to false by priya

  @Input() validDocumentTypes: string;
  @Input() allowMultipleFiles = true;
  @Input() pathSegments: string[];
  @Input() key: string;
  @Input() isPreview = false;
  @Input() buttonText: string;
  @Input() category: string;
  @Input() deleteStoredDocument = false;
  @Input() deleteDocumentMessage: string;
  @Input() showRemove = true;
  @Input() previouslyUploadedFiles: Document[] = [];
  @Input() showUploadedFilesList = true;
  @Input() attachmentTemplate=false
  @Output() uploadComplete: EventEmitter<Document[]> = new EventEmitter<Document[]>();
  @Output() fileUploaded: EventEmitter<UploadResponse> = new EventEmitter<UploadResponse>();
  @Output() fileDeleted: EventEmitter<Document> = new EventEmitter<Document>();
  templateForPermitForm:boolean=false
  markAsReview = false; // checkbox value
  @Input() requireReview = false;
  @Input() workflowApplicationDocumentId: string;

  @Output() reviewChanged = new EventEmitter<string>();
  @Output() reviewCompleted = new EventEmitter<void>();
  @Output() hasUploadChanges = new EventEmitter<boolean>();

  // Files being uploaded in the current batch
  uploadedFilesBatch: any[] = [];
  files: UploadFile[] = [];
  uploadedFiles: Document[] = [];
  uploadButtonData!: CommonButtonData;
  deleteIcon=svgs.deleteAttachmentOutlineIcon.name;

  constructor(
    public layoutService: LayoutService,
    private _documentSvc: DocumentService,
    private _toastrSvc: ToastrService,
    private workflowService : WorkflowService,
    private dataService:DataService,
    public context: WorkflowContextService,
  ) {
      //added by priya 16-12-2025

   layoutService.useNewLayout$.subscribe((useNewLayout) => {
      this.templateForPermitForm = useNewLayout;
    });
      //end by priya 16-12-2025
  }

  ngOnInit() {
    if (this.previouslyUploadedFiles && this.previouslyUploadedFiles.length > 0) {
      this.uploadedFiles = this.previouslyUploadedFiles;
    }
    this.validDocumentTypes = this.stripFileTypes(this.validDocumentTypes);

    if (!this.buttonText) {
    this.buttonText = 'SELECT FILE';
    if (this.allowMultipleFiles) {
      this.buttonText += '\(s)';
    }
  }

  this.uploadButtonData = {
    text: this.buttonText,
    onClick: () => this.onClick()
  };

  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['previouslyUploadedFiles']) {
      this.uploadedFiles = [...this.previouslyUploadedFiles];

    }
  }
  //added by priya 22-12-2025
  // ngAfterViewInit(): void {
  //   this.uploadButtonData = {
  //     text: this.buttonText,
  //     onClick: () => this.onClick()
  //   };
  // }
    //end by priya 22-12-2025

    markReviewed(doc: Document): void {
      this.reviewChanged.emit(doc.id);
    }
   stripFileTypes(fileTypes: string) {
    if (!fileTypes) {
      return '';
    }

    return fileTypes
      .split(',')
      .map(type => type.trim())
      .filter(type => type.length > 0)
      .map(type => {
        if (type.includes('/')) {
          return type.toLowerCase();
        }
        return (type.startsWith('.') ? type : '.' + type).toLowerCase();
      })
      .join(',');
  }

  public async uploadFile(
    file: UploadFile,
    fileName: string,
    modifiedName = false
  ): Promise<HttpResponse<UploadResponse>> {
    const extraData = {
      name: fileName,
      parentPath: this.pathSegments,
      filename: file.data.name,
      origName: file.data.name,
      category: this.category
    };

    file.inProgress = true;

    const res = await this._documentSvc
      .upload(file.data, extraData, modifiedName)
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              file.progress = Math.round((event.loaded / event.total) * 100);
              break;
            case HttpEventType.Response:
              return event;
          }
        }),
        catchError((error: HttpErrorResponse) => {
        file.inProgress = false;
        const apiMessage = error?.error?.error || error?.message || 'Unknown error';
        return throwError(() => new Error(apiMessage));
      })
      )
      .toPromise();

    return res;
  }

  isValidFileType(file: File): boolean {
  if (!this.validDocumentTypes) {
    return true;
  }

  const allowedTypes = this.validDocumentTypes
    .split(',')
    .map(t => t.trim().toLowerCase())
    .filter(t => t.length > 0);

  const fileName = file.name.toLowerCase();
  const fileMimeType = (file.type || '').toLowerCase();

  return allowedTypes.some(type => {
    if (type.includes('/')) {
      if (type.endsWith('/*')) {
        const prefix = type.split('/')[0];
        return fileMimeType.startsWith(prefix + '/');
      }
      return fileMimeType === type;
    }
    return fileName.endsWith(type);
  });
}

  onClick() {
    const fileUpload = this.fileUpload.nativeElement;
    fileUpload.onchange = () => {

      if (fileUpload.files && fileUpload.files.length > 0) {
        const parentElement = fileUpload.parentElement;
        if (parentElement) {
          const errorSpan = parentElement.querySelector('.upload-required-error');
          if (errorSpan) {
            errorSpan.remove();
          }
        }
      }

     let hasInvalidFile = false;
      for (let index = 0; index < fileUpload.files.length; index++) {
        const file = fileUpload.files[index];

         if (!this.isValidFileType(file)) {
        hasInvalidFile = true;
        this._toastrSvc.error(
          `"${file.name}" is not allowed. Allowed types: ${this.validDocumentTypes}`,
          'Invalid file type'
        );
        continue;
      }

        if (!this.files.some(f => f.data.name == file.name)) {
          this.files.push({
            data: file,
            inProgress: false,
            progress: 0,
            size: numeral(file.size).format('0.0 b'),
            finished: false
          });
        }
      }

       if (hasInvalidFile && this.files.length === 0) {
          fileUpload.value = null;
        }

      if (this.files.length > 0) {
        this._documentSvc.filesAdded.emit({
          files: this.files,
          key: this.key
        });

        this.uploadFiles();
      }
    };
    fileUpload.click();
  }

  async uploadFiles() {
    const filesToUpload = [...this.files.filter(f => !f.inProgress && !f.finished)];

    for (let file of filesToUpload) {
      try {
        let fileName = file.data.name;

        let modifiedName = false;

        if (this.uploadedFiles.some(uf => (uf.name || uf.documentName || '').toLowerCase() == file.data.name.toLowerCase())) {
          modifiedName = true;
          let suffixInUse = true;
          let suffix = 1;

          while (suffixInUse) {
            const separatorIndex = file.data.name.lastIndexOf('.');

            const name = file.data.name.substring(0, separatorIndex);

            const fileExtension = file.data.name.substring(separatorIndex);

            fileName = `${name}_${suffix.toString().padStart(3, '0')}${fileExtension}`;

            if (!this.uploadedFiles.some(uf => (uf.name || uf.documentName || '').toLowerCase() == fileName.toLowerCase())) {
              suffixInUse = false;
            }
            else {
              suffix++;
            }
          }
        }

        // if there are multiple simultaneous upload requests including same file, a different thread
        // could have set this file to inProgress, or finished uploading the file,
        // or finished its uploadFiles() request and removed the files from this.files.
        //
        // in any of those circumstances, we don't want to upload it
        if (!this.files.some(f => f.data.name == file.data.name)
          || this.files.some(f => f.data.name == file.data.name && (f.inProgress || f.finished))
        ) {
          continue;
        }

        let result = await this.uploadFile(file, fileName, modifiedName);

        file.finished = true;
        file.inProgress = false;

        if (result.status === 200 && result.body != null) {
          result.body.name = fileName;

          this.uploadedFilesBatch.push(result.body);

          this.fileUploaded.emit(result.body);

          this.uploadedFiles.push(result.body as any);
        }
      } catch (err: any) {
        if (err?.status !== 500) {
          this._toastrSvc.error(err.message, 'Upload failed!');
        }
        this.files = this.files.filter(f => f.data.name !== file.data.name);
      }
    }

    this.uploadComplete.emit(this.uploadedFilesBatch);

    this._documentSvc.fileUploadComplete.emit({
      files: this.uploadedFilesBatch,
      key: this.key
    });

    this.reset();
  }

  reset() {
    this.uploadedFilesBatch = [];
    this.files = [...this.files.filter(f => f.inProgress)];
    this.fileUpload.nativeElement.value = null;
  }

  clearUploaded() {
    this.uploadedFiles = [];
  }

  deleteFile(file: Document) {
    const updateFiles = () => {
      const files = this.uploadedFiles.filter(f => f !== file);

      this.uploadedFiles = files;

      this.fileDeleted.emit(file);
    };
//added by priya on 18-06-2026 delete workflow application document in attachment
    if (file?.id && !this.deleteStoredDocument) {
      this.dataService
        .deleteWorkflowApplicationDocument(file.id)
        .subscribe({
            next: () =>{
              this.hasUploadChanges.emit(true);
                updateFiles()},
          error: () => this._toastrSvc.error('Document failed to delete.')
        });

      return;
    }

    // internal and external notes use this component. Clicking delete should delete their docs
    // from storage.  Application documents don't need to be deleted from storage, their value
    // just needs updated so the application EntityData will be updated on clicking Next/Update
    // deleteStoredDocument is a flag only internal and external notes set to true
    if (this.deleteStoredDocument) {
      this._documentSvc
        .removeDocument(file.path, file) //I guess pass file.path here???
        .subscribe(result => {
          if (result) {
            updateFiles();
          } else {
            this._toastrSvc.error('Document failed to delete.');
          }
        });
    } else {
      updateFiles();
    }
  }

  // added by priya 22-12-2025
  @ViewChild('deleteModal', { static: false }) deleteModal: any;
  @Input() documentCount = 0;
  @Input() reviewRequired = false;
  deleteModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
  }
  selectedFile: any;

  openDeleteModal(file: any) {
    this.selectedFile = file;

  this.deleteModalData = {
    ...this.deleteModalData,
    open: true,
    modalDescp:this.attachmentTemplate?'Are you sure you want to delete this upload category?':'Delete Document',
    modalSubDescp:this.attachmentTemplate?'Deleting this document may result in permanent loss of uploaded documents' :`Are you sure you want to delete ${file?.name}?

The document will only be deleted once Save, Next or Update is clicked.`,
yesNoBtn: true,
replaceYesWith: 'DELETE',
replaceNoWith: 'CANCEL',

  };

  }
  closeDeleteModal = () => {
    this.deleteModalData = {
      ...this.deleteModalData,
      open: false
    };
  };

  handleDeleteFile() {
    this.closeDeleteModal();

    if (this.selectedFile) {
      this.deleteFile(this.selectedFile);
      this.selectedFile = null;
    }
  }
  formatReviewedOn(dateString: string): string {
    if (!dateString) return '';

    const cleaned = dateString
      .replace(' at', '')
      .replace(/ EDT| EST| UTC| GMT/g, '');

    const date = new Date(cleaned);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
    // end by priya 22-12-2025


}
